import { z } from 'zod';
import { ZONE_SSL_MODES } from '../domain/zone-ssl.js';

/** The record types the Domains tab edits; the rest are shown and left alone. */
export const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'TXT', 'MX', 'CAA', 'NS', 'SRV'] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

/** Types Cloudflare can put its proxy in front of. */
export const PROXIABLE_TYPES: readonly string[] = ['A', 'AAAA', 'CNAME'];

/** Types that carry a priority. */
export const PRIORITY_TYPES: readonly string[] = ['MX', 'SRV'];

const IPV4 = /^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

export const dnsRecordSchema = z.object({
  type: z.enum(DNS_RECORD_TYPES),
  /** The full name, or `@` for the domain itself. */
  name: z.string().trim().min(1).max(253).regex(/^(@|\*|[a-zA-Z0-9_*-]+)(\.[a-zA-Z0-9_-]+)*\.?$/),
  content: z.string().trim().min(1).max(4096),
  /** 1 is automatic. */
  ttl: z.number().int().refine((value) => value === 1 || (value >= 60 && value <= 86_400), 'TTL: 1 (auto) or 60–86400').default(1),
  proxied: z.boolean().optional(),
  priority: z.number().int().min(0).max(65_535).nullable().optional(),
}).strict().superRefine((record, context) => {
  if (record.type === 'A' && !IPV4.test(record.content)) context.addIssue({ code: 'custom', path: ['content'], message: 'An IPv4 address' });
  if (record.type === 'AAAA' && !/^[0-9a-fA-F:]+$/.test(record.content)) context.addIssue({ code: 'custom', path: ['content'], message: 'An IPv6 address' });
  if (record.proxied && !PROXIABLE_TYPES.includes(record.type)) context.addIssue({ code: 'custom', path: ['proxied'], message: 'Only A, AAAA and CNAME are proxied' });
  if (PRIORITY_TYPES.includes(record.type) && (record.priority === undefined || record.priority === null)) {
    context.addIssue({ code: 'custom', path: ['priority'], message: 'MX and SRV need a priority' });
  }
});
export type DnsRecordInput = z.infer<typeof dnsRecordSchema>;
export type DnsRecordRequest = z.input<typeof dnsRecordSchema>;

/** The codes a Bulk Redirect answers with: permanent 301/308, temporary 302/307. */
export const REDIRECT_CODES = [301, 302, 307, 308] as const;

export const redirectSchema = z.object({
  /** Host and path on the domain; a scheme, if given, is dropped. */
  sourceUrl: z.string().trim().min(1).max(2048),
  targetUrl: z.string().trim().max(2048).url().refine((value) => /^https?:\/\//i.test(value), 'An http:// or https:// address'),
  statusCode: z.union([z.literal(301), z.literal(302), z.literal(307), z.literal(308)]).default(301),
  preserveQueryString: z.boolean().default(true),
  preservePathSuffix: z.boolean().default(false),
  includeSubdomains: z.boolean().default(false),
  subpathMatching: z.boolean().default(false),
}).strict();
export type RedirectInput = z.infer<typeof redirectSchema>;
export type RedirectRequest = z.input<typeof redirectSchema>;

export const zoneSslSchema = z.object({ mode: z.enum(ZONE_SSL_MODES) }).strict();
export const alwaysHttpsSchema = z.object({ enabled: z.boolean() }).strict();
